import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';
import { IconButton } from '@atlaskit/button/new';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import Tooltip from '@atlaskit/tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { PageRemixModal } from '@confluence/malleable-ui-common/entry-points/PageRemixContainer.ts';
import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { isEmptyAdf } from '@confluence/comments-util/entry-points/domUtils.ts';

const i18n = defineMessages({
	remixPageLabel: {
		id: 'object-sidebar-components.remix-page.label',
		defaultMessage: 'Remix page',
		description: 'Label for the button that opens the page-level remix overlay',
	},
	remixPageTooltip: {
		id: 'object-sidebar-components.remix-page.tooltip',
		defaultMessage: 'Remix page',
		description: 'Tooltip for the button that opens the page-level remix overlay',
	},
	remixPageEmptyTooltip: {
		id: 'object-sidebar-components.remix-page.empty-tooltip',
		defaultMessage: 'Add content to this page to remix',
		description: 'Tooltip shown when the remix button is disabled because the page has no content',
	},
});

const parseAdf = (value: string): DocNode | null => {
	try {
		const parsed = JSON.parse(value);
		if (!parsed || parsed.type !== 'doc') {
			return null;
		}
		return parsed as DocNode;
	} catch {
		return null;
	}
};

export const RemixPanelButton = ({
	contentBodyValue,
	getAdfDocument,
	isEditorEmpty,
	isDisabled = false,
}: {
	contentBodyValue?: string | null;
	getAdfDocument?: () => Promise<DocNode | null>;
	isEditorEmpty?: boolean;
	isDisabled?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const { showSmartCreationModal } = useSmartCreationModalState();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isThinking, setIsThinking] = useState(false);
	const [lazyAdfDocument, setLazyAdfDocument] = useState<DocNode | null>(null);

	const parsedAdfDocument = useMemo(
		() => (contentBodyValue ? parseAdf(contentBodyValue) : null),
		[contentBodyValue],
	);

	const hasContent = getAdfDocument
		? !isEditorEmpty
		: parsedAdfDocument !== null && !isEmptyAdf(parsedAdfDocument);

	const resolvedAdfDocument = parsedAdfDocument ?? lazyAdfDocument;

	const handleClick = useCallback(async () => {
		if (!hasContent) {
			return;
		}
		if (getAdfDocument) {
			const doc = await getAdfDocument();
			if (doc) {
				setLazyAdfDocument(doc);
				setIsModalOpen(true);
			}
		} else {
			setIsModalOpen(true);
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'remixPage',
				source: 'objectSidebar',
			},
		}).fire();
	}, [createAnalyticsEvent, hasContent, getAdfDocument]);

	const closeModal = useCallback(() => {
		setIsModalOpen(false);
		setIsThinking(false);
	}, []);

	const onStartThinking = useCallback(() => {
		setIsThinking(true);
		setIsModalOpen(false);
	}, []);

	const onStopThinking = useCallback((options?: { reopenModal?: boolean }) => {
		setIsThinking(false);
		if (options?.reopenModal) {
			setIsModalOpen(true);
		}
	}, []);

	const tooltipContent = hasContent
		? formatMessage(i18n.remixPageTooltip)
		: formatMessage(i18n.remixPageEmptyTooltip);

	return (
		<>
			<Tooltip position="left" content={tooltipContent} hideTooltipOnClick>
				<IconButton
					onClick={handleClick}
					appearance="subtle"
					shape={isRovoButtonEnabled ? 'default' : 'circle'}
					icon={AiGenerativeRemixIcon}
					label={formatMessage(i18n.remixPageLabel)}
					testId="sidebar-remix-button"
					isDisabled={isDisabled || !hasContent}
				/>
			</Tooltip>
			{(isModalOpen || isThinking) && resolvedAdfDocument && (
				<ConfluenceConversationAssistant>
					<PageRemixModal
						adfDocument={resolvedAdfDocument}
						onClose={closeModal}
						onStartThinking={onStartThinking}
						onStopThinking={onStopThinking}
						isEphemeralViewVisible={showSmartCreationModal}
					/>
				</ConfluenceConversationAssistant>
			)}
		</>
	);
};
